import type { ReactNode } from 'react';
import { cx } from '../cx';
import { LABEL_TEXT, TICK_TEXT, percentOf } from './scale';
import { WIDE_ONLY, type ChartLayout } from './layout';

export type RidgelineGroup = {
  id: string;
  label: ReactNode;
  /** Counts per bin, on the bins every group shares. */
  counts: number[];
};

export type RidgelineProps = {
  groups: RidgelineGroup[];
  /** The bins' edges, one more than the counts (e.g. pay from 1,500 to 7,500 in steps of 500). */
  edges: number[];
  /** The group the headline is about: sun; the rest context. */
  highlightId?: string;
  formatTick: (value: number) => string;
  /** One sentence that states the finding, for screen readers. */
  description: string;
  /** `auto` (default) switches to the narrow form in a narrow container: each group's name sits above its ridge instead of in a left column. */
  layout?: ChartLayout;
  className?: string;
};

/** A smooth outline through the bin midpoints, closed at the baseline, in a 0 to 100 box. */
export function ridgePath(counts: number[], peak: number): string {
  const n = counts.length;
  const pts = counts.map((c, i) => [((i + 0.5) / n) * 100, 100 - (peak ? (c / peak) * 100 : 0)] as const);
  let d = `M0,100 L${pts[0][0]},${pts[0][1]}`;
  for (let i = 1; i < n; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    const mx = (x0 + x1) / 2;
    d += ` C${mx},${y0} ${mx},${y1} ${x1},${y1}`;
  }
  return `${d} L100,100 Z`;
}

/**
 * Distributions stacked as overlapping ridges, one per group, on one shared scale: where pay
 * concentrates in each field. Each row overlaps the one above it. Each ridge is scaled to its own peak, so shapes compare; counts are
 * in the table twin.
 */
const ROW: Record<ChartLayout, string> = {
  auto: 'flex flex-col gap-0.5 @md:flex-row @md:items-end @md:gap-3',
  narrow: 'flex flex-col gap-0.5',
  wide: 'flex items-end gap-3',
};
const OVERLAP: Record<ChartLayout, string> = { auto: '@md:-mt-5', narrow: '', wide: '-mt-5' };
const NAME: Record<ChartLayout, string> = {
  auto: 'w-full @md:mb-0.5 @md:w-1/4 @md:shrink-0',
  narrow: 'w-full',
  wide: 'mb-0.5 w-1/4 shrink-0',
};
// In a column, flex-1 would collapse the ridge to no height: the narrow form sizes it by width.
const RIDGE: Record<ChartLayout, string> = { auto: 'w-full @md:w-auto @md:flex-1', narrow: 'w-full', wide: 'flex-1' };

export function Ridgeline({
  groups,
  edges,
  highlightId,
  formatTick,
  layout = 'auto',
  description,
  className,
}: RidgelineProps) {
  const lo = edges[0];
  const hi = edges[edges.length - 1];
  const ticks = edges.filter((_, i) => i % Math.max(1, Math.round((edges.length - 1) / 4)) === 0);
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col', className)}>
      {groups.map((g, k) => {
        const hl = g.id === highlightId;
        return (
          <div
            key={g.id}
            className={cx(ROW[layout], k > 0 && OVERLAP[layout])}
            style={{ zIndex: k + 1 }}
            aria-hidden="true"
          >
            <span className={cx('truncate', NAME[layout], LABEL_TEXT, hl && 'font-semibold text-jc-ink')}>
              {g.label}
            </span>
            <span className={cx('relative block h-12', RIDGE[layout])}>
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                className="absolute inset-0 size-full overflow-visible"
              >
                <path
                  d={ridgePath(g.counts, Math.max(...g.counts))}
                  vectorEffect="non-scaling-stroke"
                  strokeWidth={1.5}
                  className={hl ? 'fill-jc-chart-2 stroke-jc-ink' : 'fill-jc-elevated stroke-jc-ink-secondary'}
                />
              </svg>
            </span>
          </div>
        );
      })}
      <div className="flex gap-3 pt-1" aria-hidden="true">
        <span className={cx('w-1/4 shrink-0', WIDE_ONLY[layout])} />
        <span className="relative h-4 flex-1 border-t border-jc-chart-grid">
          {ticks.map((t, i) => (
            <span
              key={t}
              className={cx(
                'absolute top-0.5 whitespace-nowrap',
                i === 0 ? '' : i === ticks.length - 1 ? '-translate-x-full' : '-translate-x-1/2',
                TICK_TEXT,
              )}
              style={{ left: `${percentOf(t, lo, hi)}%` }}
            >
              {formatTick(t)}
            </span>
          ))}
        </span>
      </div>
    </div>
  );
}
